"use client";
import { useAuthenticatedContext } from "./AuthenticatedContext";
import Fab from "@mui/material/Fab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { Fragment, useMemo, useRef, useState } from "react";
import Popover from "@mui/material/Popover";
import { usePathname, useRouter } from "next/navigation";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { getUrls } from "@portal/app/_common/lib/urls";
import { usePageContext } from "@portal/app/_components/context/PageContext";
import { CoachImage } from "@portal/app/_components/ui-elements/Coach";
import coachIcon from "@portal/app/_components/resources/images/coach/friendly.svg";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import Chip from "@mui/material/Chip";
import { InProgressBadge } from "@portal/app/dash/_components/InProgressBadge";
import { useResumableSchoolEnrollments } from "./useResumableSchoolEnrollments";
import { shouldShowInProgressReminder } from "@portal/app/dash/_components/reminderUtils";

export const InProgressProvider = () => {
  const { customerId, loggedIn, role } = useAuthenticatedContext();
  const { isDesktop } = usePageContext();
  const analytics = useMixpanel();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const inProgressRef = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const shouldShow = useMemo(
    () =>
      shouldShowInProgressReminder({
        pathname,
        loggedIn,
        customerId,
        isCustomer: role.isCustomer,
      }),
    [loggedIn, pathname, customerId, role.isCustomer]
  );
  const { enrollments } = useResumableSchoolEnrollments({
    skip: !shouldShow,
  });
  const firstFiveEnrollments = enrollments?.slice(0, 5);
  const urls = getUrls();

  if (!shouldShow || !firstFiveEnrollments?.length) {
    return null;
  }

  return (
    <InProgressContainer ref={inProgressRef}>
      <InProgressBadge count={enrollments?.length ?? 0} disabled={open}>
        <InProgressFab
          color="primary"
          variant="extended"
          onClick={(_event: React.MouseEvent<HTMLButtonElement>) => {
            setOpen(!open);
            return analytics.track("In Progress - Opened");
          }}
        >
          {open ? <FontAwesomeIcon icon={faChevronDown} /> : `Continue sign-up`}
        </InProgressFab>
      </InProgressBadge>
      <Popover
        id="in-progress-popover"
        open={open}
        anchorEl={inProgressRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            style: {
              background: "transparent",
              boxShadow: "none",
              border: "none",
              width: "100%",
              paddingTop: "1rem",
              paddingBottom: "1rem",
              ...(isDesktop && { maxWidth: "400px" }),
            },
          },
          root: {
            slotProps: {
              backdrop: {
                onClick: () => setOpen(false),
                style: {
                  background:
                    "transparent linear-gradient(to top, rgba(0, 0, 0, 0.05) 1px, rgba(0, 0, 0, .3) 1px) repeat-x bottom",
                  backdropFilter: "blur(1rem)",
                },
              },
            },
          },
        }}
      >
        <PopoverContainer>
          <Typography variant="new_h2" sx={{ m: 0 }}>
            Pick up where you left off
          </Typography>
          {firstFiveEnrollments.map((enrollment, i) => {
            const logoUrl = enrollment.provider.logoImageFile?.url;
            const isFirstEnrollment = i === 0;
            const programName =
              enrollment.program.name.length > 50
                ? `${enrollment.program.name.substring(0, 50)}...`
                : enrollment.program.name;
            const providerName =
              enrollment.provider.fullName.length > 40
                ? `${enrollment.provider.fullName.substring(0, 40)}...`
                : enrollment.provider.fullName;
            const resumeEnrollment = async () => {
              await analytics.track("In Progress - Resumed Sign Up", {
                surface: "floating reminder",
                enrollmentId: enrollment.id,
                programId: enrollment.program.id,
                providerId: enrollment.provider.id,
                vocationId: enrollment.program.vocationId,
                programName: enrollment.program.name,
                providerName: enrollment.provider.fullName,
              });
              router.push(
                urls.ProviderProgramStorefront(enrollment.provider.slug, enrollment.program.slug, {
                  autoOpen: true,
                })
              );
              setOpen(false);
            };
            return (
              <Fragment key={enrollment.id}>
                {isFirstEnrollment && (
                  <CoachContainer>
                    <CoachPhrase label="Keep going!" color="primary" size="small" />
                    <CoachImage src={coachIcon.src} size={60} />
                  </CoachContainer>
                )}
                <InProgressCard
                  role="button"
                  tabIndex={0}
                  aria-label={`Continue ${enrollment.program.name} sign-up at ${enrollment.provider.fullName}`}
                  onClick={resumeEnrollment}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      void resumeEnrollment();
                    }
                  }}
                >
                  <LogoContainer>
                    {logoUrl && (
                      <Logo>
                        <LogoImg src={logoUrl} alt="logo" />
                      </Logo>
                    )}
                  </LogoContainer>
                  <CardText>
                    <Typography variant="new_h3" lineHeight={1} margin={0} fontWeight="bold">
                      {providerName}
                    </Typography>
                    <Typography variant="subtitle2" lineHeight={1} color="text.secondary">
                      {programName}
                    </Typography>
                  </CardText>
                  <IconContainer>
                    <FontAwesomeIcon icon={faChevronRight} size="sm" />
                  </IconContainer>
                </InProgressCard>
              </Fragment>
            );
          })}
        </PopoverContainer>
      </Popover>
    </InProgressContainer>
  );
};

const InProgressContainer = styled(Box)`
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: 1301;

  & .MuiBadge-badge {
    transform: scale(1) translate(120%, -50%);
    z-index: 1302;
  }
`;

const InProgressFab = styled(Fab)`
  border-radius: 0.5rem;
  height: 3rem;
  font-size: 1rem;
  text-transform: none;
  gap: 1rem;
`;

const PopoverContainer = styled(Paper)`
  background-color: ${({ theme }) => theme.palette.dream.grayLight};
  padding: 1rem;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CoachContainer = styled(Box)`
  position: absolute;
  top: 29px;
  right: 2px;
  z-index: 0;
`;

const CoachPhrase = styled(Chip)`
  position: absolute;
  right: 50px;
  top: 2px;
  height: 1.4rem;
  font-size: 0.75rem;
  max-width: unset;

  &:after {
    content: "";
    position: absolute;
    right: 0;
    top: 50%;
    width: 0;
    height: 0;
    border: 9px solid transparent;
    border-left-color: ${({ theme }) => theme.palette.primary.dark};
    border-right: 0;
    border-bottom: 0;
    margin-top: -2px;
    margin-right: -3px;
  }
`;

const InProgressCard = styled(Paper)`
  display: flex;
  position: relative;
  height: 85px;
  justify-content: end;
  border-radius: 0.5rem;
  border: none;
  width: 100%;
`;

const LOGO_WIDTH = 35;
const LogoContainer = styled(Box)`
  display: flex;
  position: absolute;
  height: 100%;
  width: 100%;
`;

const CardText = styled(Box)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 0.5rem;
  padding: 0 1rem;
  white-space: normal;
  justify-content: center;
  max-width: 60%;
`;

const Logo = styled(Box)`
  position: relative;
  width: ${LOGO_WIDTH}%;
  margin-right: ${100 - LOGO_WIDTH}%;
`;

const LogoImg = styled("img")`
  position: absolute;
  border-radius: 0.5rem 0 0 0.5rem;
  height: 100%;
  width: 100%;
  inset: 0;
  object-fit: cover;
  object-position: left center;
  color: transparent;
`;

const IconContainer = styled(Box)`
  height: 100%;
  display: flex;
  align-items: center;
  padding-right: 1rem;
`;
